import React from "react";
import { Link } from "react-router-dom";
import "./PrivacyPolicy.css";

export default function PrivacyPolicy() {
  return (
    <main className="privacy-page" aria-labelledby="privacy-title">
      <section className="privacy-hero">
        <div className="privacy-container">
          <nav className="privacy-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Privacy Policy</span>
          </nav>

          <p className="privacy-eyebrow">Auditus-iERP</p>

          <h1 id="privacy-title">Privacy Policy</h1>

          <p className="privacy-updated">
            Last Updated: September 4, 2026
          </p>
        </div>
      </section>

      <section className="privacy-content">
        <div className="privacy-container privacy-document">

          {/* 1. Introduction */}
          <article>
            <h2>1. Introduction</h2>

            <p>
              Auditus-iERP ("Auditus-iERP", "we", "our", or "us") is a
              business software service provided by Auditus IERP.
            </p>

            <p>
              This Privacy Policy explains how Auditus-iERP collects,
              accesses, uses, stores, protects, and shares information when
              you use our application and connect your Google account or
              Gmail account to Auditus-iERP.
            </p>

            <p>
              By connecting your Google account to Auditus-iERP, you
              authorize the application to access only the Google services
              and permissions that you approve during the authorization
              process.
            </p>

            <p>
              <strong>
                Auditus-iERP's use and transfer of information received from Google APIs
                will adhere to the Google API Services User Data Policy, including the Limited Use requirements.
              </strong>
            </p>
          </article>

          {/* 2. Information We Collect */}
          <article>
            <h2>2. Information We Collect</h2>

            <p>
              Auditus-iERP may collect and process information that you
              provide directly to us or that is necessary to provide the
              services you request.
            </p>

            <p>This may include:</p>

            <ul>
              <li>Your name and contact information.</li>
              <li>Your email address.</li>
              <li>Your business and account information.</li>
              <li>Information required to authenticate your account.</li>
              <li>
                Information necessary to provide the features and services
                requested by you.
              </li>
              <li>
                Information related to your use of Auditus-iERP.
              </li>
            </ul>

            <p>
              When you connect a Google account, Auditus-iERP may receive
              information from Google according to the permissions that you
              authorize.
            </p>
          </article>

          {/* 3. Google Account Information */}
          <article>
            <h2>3. Google Account Information</h2>

            <p>
              When you connect your Google account to Auditus-iERP, we may
              access basic Google account information that is necessary to
              authenticate and maintain your account connection.
            </p>

            <p>This may include:</p>

            <ul>
              <li>Your Google account email address.</li>
              <li>Your basic Google profile information.</li>
              <li>
                Authentication information necessary to establish the
                authorized connection.
              </li>
            </ul>

            <p>
              Auditus-iERP requests only the Google permissions necessary to
              provide the functionality used by you.
            </p>
          </article>

          {/* 4. Gmail API Access */}
          <article>
            <h2>4. Gmail API Access</h2>

            <p>
              Auditus-iERP uses the Google Gmail API to provide Gmail-related
              functionality.
            </p>

            <p>
              The Gmail permission requested by Auditus-iERP is:
            </p>

            <p>
              <code>
                https://www.googleapis.com/auth/gmail.send
              </code>
            </p>

            <p>
              This permission allows Auditus-iERP to send emails through the
              Gmail account that you authorize.
            </p>

            <p>
              Auditus-iERP uses this permission only to provide email-sending
              functionality requested or initiated by you.
            </p>

            <p>
              For example, Auditus-iERP may use the Gmail API to send business
              emails, notifications, invoices, reports, or other emails that
              you initiate through the application.
            </p>

            <p>
              The application does not request Gmail mailbox-reading
              permissions when they are not required for the functionality
              provided by Auditus-iERP.
            </p>
          </article>

          {/* 5. How We Use Google User Data */}
          <article>
            <h2>5. How We Use Google User Data</h2>

            <p>
              Information received from Google APIs is used only for purposes
              necessary to provide the functionality of Auditus-iERP.
            </p>

            <p>Google user data may be used to:</p>

            <ul>
              <li>Authenticate your account.</li>
              <li>Connect your Google account to Auditus-iERP.</li>
              <li>Maintain your authorized Gmail connection.</li>
              <li>
                Send emails that you request through Auditus-iERP.
              </li>
              <li>
                Provide the Gmail-related functionality you have authorized.
              </li>
              <li>Maintain application security.</li>
              <li>Provide customer support when necessary.</li>
              <li>
                Maintain and improve the functionality and reliability of
                Auditus-iERP.
              </li>
            </ul>

            <p>
              Auditus-iERP does not use Google user data for advertising.
            </p>

            <p>
              Auditus-iERP does not sell Google user data.
            </p>

            <p>
              Auditus-iERP does not use Google user data for purposes
              unrelated to providing the functionality authorized by the
              user.
            </p>

            <p>
              Auditus-iERP does not use Google user data to train or improve
              generalized artificial intelligence or machine learning models.
            </p>
          </article>

          {/* 6. Gmail Message Data */}
          <article>
            <h2>6. Gmail Message Data</h2>

            <p>
              When you create and send an email through Auditus-iERP, the
              application may process the information necessary to send that
              email through Gmail.
            </p>

            <p>
              This may include information such as the recipient email
              address, subject, message content, and attachments when
              applicable.
            </p>

            <p>
              This information is processed only to provide the email-sending
              functionality requested by you.
            </p>

            <p>
              Auditus-iERP does not sell Gmail message content and does not
              use Gmail message content for advertising.
            </p>

            <p>
              Auditus-iERP does not use Gmail message data for purposes
              unrelated to providing the requested functionality.
            </p>

            <p>
              Auditus-iERP does not use the Gmail send permission to read,
              modify, delete, or manage your Gmail mailbox.
            </p>
          </article>

          {/* 7. Data Storage and Retention */}
          <article>
            <h2>7. Data Storage and Retention</h2>

            <p>
              Auditus-iERP retains information only for as long as reasonably
              necessary to provide the requested services, maintain security,
              comply with applicable legal obligations, resolve disputes,
              and enforce applicable agreements.
            </p>

            <p>
              Where necessary to maintain an authorized connection with your
              Google account, Auditus-iERP may securely store OAuth
              authorization information, such as access or refresh tokens.
            </p>

            <p>
              OAuth authorization information is protected using reasonable
              security measures and is used only to provide the Google
              functionality that you have authorized.
            </p>

            <p>
              Auditus-iERP does not permanently store Gmail message content
              solely for the purpose of providing the Gmail sending
              functionality.
            </p>

            <p>
              When information is no longer required for the purposes
              described in this Privacy Policy, Auditus-iERP may delete or
              securely dispose of that information, subject to applicable
              legal and operational requirements.
            </p>
          </article>

          {/* 8. Data Sharing */}
          <article>
            <h2>8. Data Sharing</h2>

            <p>
              Auditus-iERP does not sell your personal information, Google
              account information, or Gmail data.
            </p>

            <p>
              Auditus-iERP does not share Google user data with third parties
              except when necessary to:
            </p>

            <ul>
              <li>Provide services requested by you.</li>
              <li>Operate and maintain the application and infrastructure.</li>
              <li>Protect the security and integrity of our services.</li>
              <li>
                Comply with applicable laws, regulations, legal processes, or
                governmental requests.
              </li>
              <li>
                Protect the rights, property, or safety of Auditus-iERP, our
                users, or others.
              </li>
            </ul>

            <p>
              Service providers that support the operation of Auditus-iERP
              may process information only as necessary to provide their
              services and are expected to maintain appropriate security and
              confidentiality.
            </p>
          </article>

          {/* 9. Google API Services User Data Policy */}
          <article>
            <h2>9. Google API Services User Data Policy</h2>

            <p>
              Auditus-iERP's use and transfer of information received from
              Google APIs complies with the Google API Services User Data
              Policy, including the Limited Use requirements.
            </p>

            <p>
              Auditus-iERP requests only the Google permissions necessary to
              provide the functionality requested by users.
            </p>

            <p>
              Google user data is not sold.
            </p>

            <p>
              Google user data is not used for advertising.
            </p>

            <p>
              Google user data is not used for purposes unrelated to
              providing the functionality authorized by the user.
            </p>

            <p>
              Google user data is not used to train or improve generalized
              artificial intelligence or machine learning models.
            </p>

            <p>
              Access to Google user data is limited to the purposes described
              in this Privacy Policy and to the functionality authorized by
              the user.
            </p>
          </article>

          {/* 10. Security */}
          <article>
            <h2>10. Security</h2>

            <p>
              Auditus-iERP takes reasonable technical and organizational
              measures to protect personal information and Google user data
              against unauthorized access, disclosure, alteration, loss, or
              destruction.
            </p>

            <p>
              We take reasonable measures to protect authentication
              information and OAuth authorization information.
            </p>

            <p>
              Access to Google-authorized functionality is limited to the
              purposes described in this Privacy Policy.
            </p>

            <p>
              Although we take reasonable measures to protect your
              information, no internet-based service can guarantee absolute
              security.
            </p>
          </article>

          {/* 11. Disconnecting Google Account */}
          <article>
            <h2>11. Disconnecting Your Google Account</h2>

            <p>
              You may disconnect your Google account from Auditus-iERP at any
              time through the application's account settings, where
              available.
            </p>

            <p>
              You may also revoke Auditus-iERP's access to your Google
              account through your Google Account security settings.
            </p>

            <p>
              After Google authorization is revoked, Auditus-iERP will no
              longer be able to use the revoked Google permissions.
            </p>
          </article>

          {/* 12. Data Deletion */}
          <article>
            <h2>12. Data Deletion</h2>

            <p>
              You may request deletion of applicable personal information
              associated with your Auditus-iERP account by contacting us at
              the email address provided below.
            </p>

            <p>
              Where applicable, we will delete or anonymize information that
              is no longer required for providing our services, maintaining
              security, or complying with legal obligations.
            </p>

            <p>
              Revoking Google access does not automatically require deletion
              of all account information that Auditus-iERP is legally or
              operationally required to retain.
            </p>
          </article>

          {/* 13. Your Rights */}
          <article>
            <h2>13. Your Rights</h2>

            <p>
              Depending on applicable law, you may have rights regarding your
              personal information, including the right to:
            </p>

            <ul>
              <li>Request access to personal information we hold about you.</li>
              <li>Request correction of inaccurate information.</li>
              <li>Request deletion of applicable information.</li>
              <li>
                Request information about how your data is collected and
                used.
              </li>
              <li>Withdraw authorization for Google account access.</li>
              <li>
                Contact us regarding privacy questions or concerns.
              </li>
            </ul>

            <p>
              Some requests may be subject to applicable legal, security,
              contractual, or operational requirements.
            </p>
          </article>

          {/* 14. Third-Party Services */}
          <article>
            <h2>14. Third-Party Services</h2>

            <p>
              Auditus-iERP may use third-party service providers for hosting,
              infrastructure, security, analytics, databases, or other
              services required to operate and maintain the application.
            </p>

            <p>
              These service providers may process information only as
              necessary to provide their services to Auditus-iERP and are
              expected to maintain appropriate security and confidentiality.
            </p>

            <p>
              Auditus-iERP's use of Google APIs is also subject to Google's
              applicable policies, including the Google API Services User
              Data Policy.
            </p>
          </article>

          {/* 15. Children's Privacy */}
          <article>
            <h2>15. Children's Privacy</h2>

            <p>
              Auditus-iERP is intended for business users and is not directed
              toward children.
            </p>

            <p>
              We do not knowingly collect personal information from children
              where such collection is prohibited by applicable law.
            </p>
          </article>

          {/* 16. Changes */}
          <article>
            <h2>16. Changes to This Privacy Policy</h2>

            <p>
              We may update this Privacy Policy from time to time to reflect
              changes to our services, technology, legal requirements, or
              business practices.
            </p>

            <p>
              When changes are made, we will update the "Last Updated" date
              shown at the beginning of this Privacy Policy.
            </p>

            <p>
              We encourage you to review this Privacy Policy periodically.
            </p>
          </article>

          {/* 17. Contact */}
          <article>
            <h2>17. Contact Us</h2>

            <p>
              If you have questions about this Privacy Policy, Google account
              access, Gmail permissions, data deletion, or how your
              information is handled, please contact us.
            </p>

            <p>
              <strong>Auditus IERP</strong>
              <br />
              Website:{" "}
              <a href="https://auditusierp.com">
                https://auditusierp.com
              </a>
              <br />
              Email:{" "}
              <a href="mailto:auditusierp@gmail.com">
                auditusierp@gmail.com
              </a>
            </p>
          </article>

          {/* 18. Limited Use Commitment */}
          <article>
            <h2>18. Google User Data Limited Use Commitment</h2>

            <p>
              Auditus-iERP is committed to protecting the privacy and security
              of information received from Google APIs.
            </p>

            <p>
              We use Google user data only to provide the functionality that
              the user has authorized.
            </p>

            <p>
              We do not sell Google user data.
            </p>

            <p>
              We do not use Google user data for advertising.
            </p>

            <p>
              We do not transfer Google user data to third parties except as
              necessary to provide the requested functionality, comply with
              applicable law, or protect the security and rights of our users
              and services.
            </p>

            <p>
              Our handling of Google user data is subject to the Google API
              Services User Data Policy and its Limited Use requirements.
            </p>

            <p>
              Google user data is not used to train or improve generalized
              artificial intelligence or machine learning models.
            </p>
          </article>

        </div>
      </section>
    </main>
  );
}
